import { Component, inject, ChangeDetectionStrategy } from '@angular/core'
import {
  MatBottomSheetRef,
  MAT_BOTTOM_SHEET_DATA,
} from '@angular/material/bottom-sheet'
import { MatDivider } from '@angular/material/divider'
import { MatListModule } from '@angular/material/list'
import { Router } from '@angular/router'
import { TranslationService } from '../../../../../services/translation.service'
import { FprTemplate } from '../../../../../types/fprTemplates'
import { FPR_SYSTEM_FIELDS } from '../../../../../constants/reports'

interface FieldMapping {
  systemField: string
  alias: string
}

@Component({
  selector: 'app-fpr-field-mapping-sheet',
  imports: [MatListModule, MatDivider],
  templateUrl: './fpr-field-mapping-sheet.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './fpr-field-mapping-sheet.component.scss',
})
export class FprFieldMappingSheetComponent {
  data = inject(MAT_BOTTOM_SHEET_DATA)
  router = inject(Router)
  translationService = inject(TranslationService)

  private _bottomSheetRef =
    inject<MatBottomSheetRef<FprFieldMappingSheetComponent>>(MatBottomSheetRef)

  fprTemplate?: FprTemplate
  fields: Array<FieldMapping> = []

  // the object injected below has to be any since 'data' is dynamic

  constructor() {
    const data = this.data

    const tmpl: FprTemplate | undefined = data?.fprTemplate || data?.template
    if (tmpl) {
      this.fprTemplate = tmpl
      this.fields = this.mapFprTemplateToFieldMappings(tmpl)
    }
  }

  /**
   * Handles the `keydown` event for keyboard navigation.
   * Allows navigating the list using arrow keys and selecting items with Enter.
   *
   * @param {KeyboardEvent} event - The keyboard event triggered when a key is pressed down.
   * @param {number} index - The current index of the focused list item.
   */
  onKeydown(event: KeyboardEvent, index: number): void {
    const listItems = document.querySelectorAll('a[mat-list-item]')

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (index < listItems.length - 1)
        (listItems[index + 1] as HTMLElement).focus()
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (index > 0) (listItems[index - 1] as HTMLElement).focus()
    }
  }

  mapFprTemplateToFieldMappings(template: FprTemplate): FieldMapping[] {
    return [
      { systemField: FPR_SYSTEM_FIELDS.SKU, alias: template.sku },
      {
        systemField:
          this.translationService.getSupplierText?.() ??
          FPR_SYSTEM_FIELDS.SUPPLIER,
        alias: template.supplier,
      },

      // Core quantities
      {
        systemField:
          this.translationService.getQtyManufacturedInCanadaText?.() ??
          FPR_SYSTEM_FIELDS.QTY_MANUFACTURED_CANADA,
        alias: template.qtyManufactured,
      },
      {
        systemField:
          this.translationService.getQtyImportedText?.() ??
          FPR_SYSTEM_FIELDS.QTY_IMPORTED,
        alias: template.qtyImported,
      },
      {
        systemField:
          this.translationService.getQtyPlacedOnMarketText?.() ??
          FPR_SYSTEM_FIELDS.QTY_PLACED_ON_MARKET,
        alias: template.qtyPlacedOnMarket,
      },
      {
        systemField: FPR_SYSTEM_FIELDS.QTY_WASTE_GENERATED_AT_PREMISES,
        alias: template.qtyWasteGeneratedAtPremises,
      },
      {
        systemField: FPR_SYSTEM_FIELDS.QTY_COLLECTED_AT_END_OF_LIFE,
        alias: template.qtyCollectedAtEndOfLife,
      },

      // Diversion pathways
      {
        systemField: FPR_SYSTEM_FIELDS.QTY_MECHANICAL_RECYCLING,
        alias: template.qtyDivertedMechanicalRecycling,
      },
      {
        systemField: FPR_SYSTEM_FIELDS.QTY_CHEMICAL_RECYCLING,
        alias: template.qtyDivertedChemicalRecycling,
      },
      {
        systemField: FPR_SYSTEM_FIELDS.QTY_PROCESSING_INTO_CHEMICALS_FUELS,
        alias: template.qtyDivertedProcessingIntoChemicalsFuels,
      },
      {
        systemField: FPR_SYSTEM_FIELDS.QTY_COMPOSTING,
        alias: template.qtyDivertedComposting,
      },
      {
        systemField: FPR_SYSTEM_FIELDS.QTY_DIRECT_REUSE,
        alias: template.qtyDivertedDirectReuse,
      },
      {
        systemField: FPR_SYSTEM_FIELDS.QTY_REFURBISHMENT,
        alias: template.qtyDivertedRefurbishment,
      },
      {
        systemField: FPR_SYSTEM_FIELDS.QTY_REMANUFACTURING,
        alias: template.qtyDivertedRemanufacturing,
      },
      {
        systemField: FPR_SYSTEM_FIELDS.QTY_REPAIR,
        alias: template.qtyDivertedRepair,
      },
      {
        systemField: FPR_SYSTEM_FIELDS.QTY_INCINERATION_INDUSTRIAL_PROCESSES,
        alias: template.qtyDivertedIncinerationIndustrialProcesses,
      },
      {
        systemField: FPR_SYSTEM_FIELDS.QTY_INCINERATION_WITH_ENERGY_RECOVERY,
        alias: template.qtyDivertedIncinerationWithEnergyRecovery,
      },

      // Final disposal
      {
        systemField: FPR_SYSTEM_FIELDS.QTY_INCINERATION_NO_ENERGY,
        alias: template.qtyFinalDisposalIncinerationNoEnergy,
      },
      {
        systemField: FPR_SYSTEM_FIELDS.QTY_LANDFILL,
        alias: template.qtyFinalDisposalLandfill,
      },

      // Provincial / Territorial splits
      {
        systemField:
          this.translationService.getQtyAlbertaText?.() ??
          FPR_SYSTEM_FIELDS.QTY_ALBERTA,
        alias: template.qtyAlberta,
      },
      {
        systemField:
          this.translationService.getQtyBritishColumbiaText?.() ??
          FPR_SYSTEM_FIELDS.QTY_BRITISH_COLUMBIA,
        alias: template.qtyBritishColumbia,
      },
      {
        systemField:
          this.translationService.getQtyManitobaText?.() ??
          FPR_SYSTEM_FIELDS.QTY_MANITOBA,
        alias: template.qtyManitoba,
      },
      {
        systemField:
          this.translationService.getQtyNewBrunswickText?.() ??
          FPR_SYSTEM_FIELDS.QTY_NEW_BRUNSWICK,
        alias: template.qtyNewBrunswick,
      },
      {
        systemField:
          this.translationService.getQtyNewfoundlandAndLabradorText?.() ??
          FPR_SYSTEM_FIELDS.QTY_NEWFOUNDLAND_AND_LABRADOR,
        alias: template.qtyNewfoundlandAndLabrador,
      },
      {
        systemField:
          this.translationService.getQtyNorthwestTerritoriesText?.() ??
          FPR_SYSTEM_FIELDS.QTY_NORTHWEST_TERRITORIES,
        alias: template.qtyNorthwestTerritories,
      },
      {
        systemField:
          this.translationService.getQtyNovaScotiaText?.() ??
          FPR_SYSTEM_FIELDS.QTY_NOVA_SCOTIA,
        alias: template.qtyNovaScotia,
      },
      {
        systemField:
          this.translationService.getQtyNunavutText?.() ??
          FPR_SYSTEM_FIELDS.QTY_NUNAVUT,
        alias: template.qtyNunavut,
      },
      {
        systemField:
          this.translationService.getQtyOntarioText?.() ??
          FPR_SYSTEM_FIELDS.QTY_ONTARIO,
        alias: template.qtyOntario,
      },
      {
        systemField:
          this.translationService.getQtyPrinceEdwardIslandText?.() ??
          FPR_SYSTEM_FIELDS.QTY_PRINCE_EDWARD_ISLAND,
        alias: template.qtyPrinceEdwardIsland,
      },
      {
        systemField:
          this.translationService.getQtyQuebecText?.() ??
          FPR_SYSTEM_FIELDS.QTY_QUEBEC,
        alias: template.qtyQuebec,
      },
      {
        systemField:
          this.translationService.getQtySaskatchewanText?.() ??
          FPR_SYSTEM_FIELDS.QTY_SASKATCHEWAN,
        alias: template.qtySaskatchewan,
      },
      {
        systemField:
          this.translationService.getQtyYukonText?.() ??
          FPR_SYSTEM_FIELDS.QTY_YUKON,
        alias: template.qtyYukon,
      },
    ]
  }
}
